"use client";

import { ArrowRight, ChefHat, Flame, UtensilsCrossed } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HowToAccess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    clock: "NYC · 08:00 · MON",
    title: "Initial Assessment",
    description: "We begin with a deep dive into your operational requirements. Our team maps out the project scope, ensuring every detail aligns with your strategic goals.",
    Icon: ChefHat
  }, {
    number: "02",
    clock: "NYC · 10:30 · MON",
    title: "Strategic Execution",
    description: "Precision engineering meets creative design. We implement robust solutions, focusing on scalability, performance, and seamless integration with your existing systems.",
    Icon: UtensilsCrossed
  }, {
    number: "03",
    clock: "NYC · 16:00 · MON",
    title: "Final Delivery",
    description: "Rigorous testing and quality assurance ensure a flawless launch. We hand over the keys, providing full documentation and support for your new infrastructure.",
    Icon: Flame
  }];
  return <section data-section-id="1778" id="how-to-access" className="bg-background text-foreground py-24 md:py-32 font-default">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span aria-hidden="true" className="inline-block size-2 bg-primary" />
              <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                &sect; No 02 / Our Process
              </span>
            </div>

            <h2 className="font-default font-bold uppercase leading-[0.92] tracking-tight text-[clamp(2.5rem,5.5vw,5.25rem)]">
              How We
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">Deliver</span>
                <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1 h-[6px] bg-primary/60" />
              </span>{" "}
              Results
            </h2>
          </div>

          {}
          <div className="lg:col-span-8 lg:pt-6">
            <p className="font-default text-[clamp(1.125rem,1.3vw,1.3125rem)] leading-[1.55] text-foreground/90 max-w-[55ch]">
              Three phases, one objective. No unnecessary complexity, no hidden 
              costs, no project bloat. We define the scope, we build the solution, 
              we deliver the value. The work is the work — executed with 
              professionalism and technical excellence.
            </p>

            <div className="mt-8">
              <Button asChild variant="ghost" className="h-auto p-0 bg-transparent hover:bg-transparent text-foreground font-mono text-sm uppercase tracking-[0.08em] group">
                <Link to="/CapabilitiesPage" className="inline-flex items-center gap-3">
                  <span className="relative pb-1.5">
                    View Capabilities
                    <span aria-hidden="true" className="absolute left-0 right-0 bottom-0 h-[2px] bg-primary transition-all duration-300 group-hover:bottom-[-2px]" />
                  </span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20">
          <div aria-hidden="true" className="h-[14px] w-full" style={{
          backgroundImage: "repeating-linear-gradient(45deg, #C9A227 0 10px, #0F1B2D 10px 20px)"
        }} />
        </div>

        {}
        <div ref={gridRef} className={`mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-px bg-border/40 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {steps.map((step, i) => {
          const {
            Icon
          } = step;
          return <article data-index={i} key={step.number} className="group relative bg-card text-card-foreground border border-border/40 hover:border-primary transition-colors duration-300 ease-out hover:-translate-y-0.5 transform-gpu p-8 md:p-10 min-h-[420px] flex flex-col" style={{
            transitionDelay: `${i * 80}ms`
          }}>
                {}
                <div aria-hidden="true" className="absolute left-0 right-0 top-0 h-3" style={{
              backgroundImage: "repeating-linear-gradient(45deg, hsl(216 40% 20% / 0.25) 0 3px, transparent 3px 6px), repeating-linear-gradient(-45deg, hsl(216 40% 20% / 0.25) 0 3px, transparent 3px 6px)",
              backgroundColor: "#0F1B2D"
            }} />

                {}
                <span aria-hidden="true" className="absolute top-3 left-3 size-1.5 rounded-lg bg-border group-hover:bg-primary transition-colors duration-300" />
                <span aria-hidden="true" className="absolute top-3 right-3 size-1.5 rounded-lg bg-border group-hover:bg-primary transition-colors duration-300" />
                <span aria-hidden="true" className="absolute bottom-3 left-3 size-1.5 rounded-lg bg-border group-hover:bg-primary transition-colors duration-300" />
                <span aria-hidden="true" className="absolute bottom-3 right-3 size-1.5 rounded-lg bg-border group-hover:bg-primary transition-colors duration-300" />

                {}
                <div className="flex items-start justify-between mt-6">
                  <span className="font-default font-bold uppercase leading-none text-primary" style={{
                fontSize: "clamp(4.5rem, 7vw, 7rem)"
              }} aria-hidden="true">
                    {step.number}
                  </span>
                  <span className="font-mono text-[0.7rem] tracking-[0.08em] uppercase text-muted-foreground pt-3">
                    {step.clock}
                  </span>
                </div>

                {}
                <h3 className="mt-6 font-default font-bold uppercase leading-[1.0] tracking-tight text-[clamp(1.5rem,2.2vw,2.25rem)]">
                  {step.title}
                </h3>

                {}
                <p className="mt-4 font-default text-base leading-[1.6] text-card-foreground/80">
                  {step.description}
                </p>

                {}
                <div className="flex-1" />

                {}
                <div className="mt-8 flex items-center gap-4">
                  <Icon className="size-6 text-primary" strokeWidth={1.5} aria-hidden="true" />
                  <div className="flex-1 flex items-center">
                    <span aria-hidden="true" className="block size-1.5 bg-primary" />
                    <span aria-hidden="true" className="flex-1 h-px bg-border" />
                    <span aria-hidden="true" className="block size-1.5 bg-primary" />
                  </div>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}